import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { XIcon, PlusIcon, TrashIcon } from 'lucide-react';
interface AddInventoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (inventoryData: any) => void;
}
export function AddInventoryModal({
  isOpen,
  onClose,
  onSave
}: AddInventoryModalProps) {
  const {
    t
  } = useTranslation('shops');
  const [roomType, setRoomType] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [availableRooms, setAvailableRooms] = useState('');
  const [closedToArrivalDates, setClosedToArrivalDates] = useState<string[]>([]);
  const [closedToDepartureDates, setClosedToDepartureDates] = useState<string[]>([]);
  const [blackoutDates, setBlackoutDates] = useState<string[]>([]);
  const [newClosedToArrivalDate, setNewClosedToArrivalDate] = useState('');
  const [newClosedToDepartureDate, setNewClosedToDepartureDate] = useState('');
  const [newBlackoutDate, setNewBlackoutDate] = useState('');
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);
  if (!isOpen) return null;
  const handleAddClosedToArrivalDate = () => {
    if (newClosedToArrivalDate && !closedToArrivalDates.includes(newClosedToArrivalDate)) {
      setClosedToArrivalDates([...closedToArrivalDates, newClosedToArrivalDate]);
      setNewClosedToArrivalDate('');
    }
  };
  const handleAddClosedToDepartureDate = () => {
    if (newClosedToDepartureDate && !closedToDepartureDates.includes(newClosedToDepartureDate)) {
      setClosedToDepartureDates([...closedToDepartureDates, newClosedToDepartureDate]);
      setNewClosedToDepartureDate('');
    }
  };
  const handleAddBlackoutDate = () => {
    if (newBlackoutDate && !blackoutDates.includes(newBlackoutDate)) {
      setBlackoutDates([...blackoutDates, newBlackoutDate]);
      setNewBlackoutDate('');
    }
  };
  const handleRemoveClosedToArrivalDate = (date: string) => {
    setClosedToArrivalDates(closedToArrivalDates.filter(d => d !== date));
  };
  const handleRemoveClosedToDepartureDate = (date: string) => {
    setClosedToDepartureDates(closedToDepartureDates.filter(d => d !== date));
  };
  const handleRemoveBlackoutDate = (date: string) => {
    setBlackoutDates(blackoutDates.filter(d => d !== date));
  };
  const handleSubmit = () => {
    const inventoryData = {
      roomType,
      fromDate,
      toDate,
      availableRooms: parseInt(availableRooms, 10) || 0,
      closedToArrivalDates,
      closedToDepartureDates,
      blackoutDates
    };
    onSave(inventoryData);
    onClose();
  };
  return <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true" aria-labelledby="add-inventory-title" data-id="element-10380">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-10381">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true" onClick={onClose} data-id="element-10382">
          <div className="absolute inset-0 bg-gray-500 opacity-75" data-id="element-10383"></div>
        </div>
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full" data-id="element-10384">
          <div className="flex justify-between items-center border-b border-gray-200 px-6 py-4" data-id="element-10385">
            <h3 id="add-inventory-title" className="text-lg font-medium text-gray-900" data-id="element-10386">
              {t('components.AddInventoryModal.add_inventory')}
            </h3>
            <button onClick={onClose} aria-label={t('components.AddInventoryModal.add_inventory')} className="text-gray-400 hover:text-gray-500" data-id="element-10387">
              <XIcon size={20} aria-hidden="true" data-id="element-10388" />
            </button>
          </div>
          <div className="p-6" data-id="element-10389">
            <p className="text-sm text-gray-500 mb-4" data-id="element-10390">
              {t('components.AddInventoryModal.select_the_room_type_and_applicable')}
            </p>
            <div className="space-y-4" data-id="element-10391">
              {/* Room Type */}
              <div data-id="element-10392">
                <label htmlFor="ai-room-type" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-10393">
                  {t('components.AddInventoryModal.room_type')}
                </label>
                <div className="relative" data-id="element-10394">
                  <select id="ai-room-type" className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 appearance-none focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={roomType} onChange={e => setRoomType(e.target.value)} data-id="element-10395">
                    <option value="" data-id="element-10396">
                      {t('components.AddInventoryModal.select_room_type')}
                    </option>
                    <option value="premium-overwater" data-id="element-10397">
                      {t('components.AddInventoryModal.premium_overwater_villa')}
                    </option>
                    <option value="water-villa" data-id="element-10398">
                      {t('components.AddInventoryModal.water_villa')}
                    </option>
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none" data-id="element-10399">
                    <ChevronDownIcon size={16} className="text-gray-400" data-id="element-10400" />
                  </div>
                </div>
              </div>
              {/* From Date */}
              <div data-id="element-10401">
                <label htmlFor="ai-from-date" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-10402">
                  {t('components.AddInventoryModal.from')}
                </label>
                <input id="ai-from-date" type="text" placeholder={t('components.AddInventoryModal.attrs.mmddyyyy')} className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={fromDate} onChange={e => setFromDate(e.target.value)} data-id="element-10403" />
              </div>
              {/* To Date */}
              <div data-id="element-10404">
                <label htmlFor="ai-to-date" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-10405">
                  {t('components.AddInventoryModal.to')}
                </label>
                <input id="ai-to-date" type="text" placeholder={t('components.AddInventoryModal.attrs.mmddyyyy')} className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={toDate} onChange={e => setToDate(e.target.value)} data-id="element-10406" />
              </div>
              {/* Available Rooms */}
              <div data-id="element-10407">
                <label htmlFor="ai-available-rooms" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-10408">
                  {t('components.AddInventoryModal.daily_available_rooms')}
                </label>
                <input id="ai-available-rooms" type="number" placeholder={t('components.AddInventoryModal.attrs.enter_daily_available_rooms')} className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={availableRooms} onChange={e => setAvailableRooms(e.target.value)} data-id="element-10409" />
              </div>
              {/* Closed to Arrival dates */}
              <div data-id="element-10410">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-10411">
                  {t('components.AddInventoryModal.closed_to_arrival_dates_in_this')}
                </label>
                <div className="space-y-2" data-id="element-10412">
                  {closedToArrivalDates.map((date, index) => <div key={index} className="flex items-center" data-id="element-10413">
                      <input type="text" value={date} readOnly className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 bg-gray-50" data-id="element-10414" />
                      <button onClick={() => handleRemoveClosedToArrivalDate(date)} className="ml-2 text-red-500" aria-label={`Remove ${date}`} data-id="element-10415">
                        <TrashIcon size={16} data-id="element-10416" />
                      </button>
                    </div>)}
                  <div className="flex items-center" data-id="element-10417">
                    <input type="text" placeholder={t('components.AddInventoryModal.attrs.mmddyyyy')} className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={newClosedToArrivalDate} onChange={e => setNewClosedToArrivalDate(e.target.value)} data-id="element-10418" />
                    <button onClick={handleAddClosedToArrivalDate} className="ml-2 text-red-500" data-id="element-10419">
                      <TrashIcon size={16} data-id="element-10420" />
                    </button>
                  </div>
                  <button onClick={handleAddClosedToArrivalDate} className="flex items-center text-red-600 text-sm font-medium" data-id="element-10421">
                    <PlusIcon size={16} className="mr-1" data-id="element-10422" />
                    {t('components.AddInventoryModal.add')}
                  </button>
                </div>
              </div>
              {/* Closed to Departure dates */}
              <div data-id="element-10423">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-10424">
                  {t('components.AddInventoryModal.closed_to_departure_dates_in_this')}
                </label>
                <div className="space-y-2" data-id="element-10425">
                  {closedToDepartureDates.map((date, index) => <div key={index} className="flex items-center" data-id="element-10426">
                      <input type="text" value={date} readOnly className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 bg-gray-50" data-id="element-10427" />
                      <button onClick={() => handleRemoveClosedToDepartureDate(date)} className="ml-2 text-red-500" aria-label={`Remove ${date}`} data-id="element-10428">
                        <TrashIcon size={16} data-id="element-10429" />
                      </button>
                    </div>)}
                  <div className="flex items-center" data-id="element-10430">
                    <input type="text" placeholder={t('components.AddInventoryModal.attrs.mmddyyyy')} className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={newClosedToDepartureDate} onChange={e => setNewClosedToDepartureDate(e.target.value)} data-id="element-10431" />
                    <button onClick={handleRemoveClosedToDepartureDate} className="ml-2 text-red-500" data-id="element-10432">
                      <TrashIcon size={16} data-id="element-10433" />
                    </button>
                  </div>
                  <button onClick={handleAddClosedToDepartureDate} className="flex items-center text-red-600 text-sm font-medium" data-id="element-10434">
                    <PlusIcon size={16} className="mr-1" data-id="element-10435" />
                    {t('components.AddInventoryModal.add')}
                  </button>
                </div>
              </div>
              {/* Blackout dates */}
              <div data-id="element-10436">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-10437">
                  {t('components.AddInventoryModal.blackout_dates_in_this_period')}
                </label>
                <div className="space-y-2" data-id="element-10438">
                  {blackoutDates.map((date, index) => <div key={index} className="flex items-center" data-id="element-10439">
                      <input type="text" value={date} readOnly className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 bg-gray-50" data-id="element-10440" />
                      <button onClick={() => handleRemoveBlackoutDate(date)} className="ml-2 text-red-500" aria-label={`Remove ${date}`} data-id="element-10441">
                        <TrashIcon size={16} data-id="element-10442" />
                      </button>
                    </div>)}
                  <div className="flex items-center" data-id="element-10443">
                    <input type="text" placeholder={t('components.AddInventoryModal.attrs.mmddyyyy')} className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={newBlackoutDate} onChange={e => setNewBlackoutDate(e.target.value)} data-id="element-10444" />
                    <button onClick={handleRemoveBlackoutDate} className="ml-2 text-red-500" data-id="element-10445">
                      <TrashIcon size={16} data-id="element-10446" />
                    </button>
                  </div>
                  <button onClick={handleAddBlackoutDate} className="flex items-center text-red-600 text-sm font-medium" data-id="element-10447">
                    <PlusIcon size={16} className="mr-1" data-id="element-10448" />
                    {t('components.AddInventoryModal.add')}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-6 py-4 flex justify-end" data-id="element-10449">
            <button onClick={handleSubmit} className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500" data-id="element-10450">
              {t('components.AddInventoryModal.create')}
            </button>
          </div>
        </div>
      </div>
    </div>;
}
// Helper component for the ChevronDown icon
function ChevronDownIcon({
  size = 24,
  className = ''
}) {
  return <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} data-id="element-10451">
      <polyline points="6 9 12 15 18 9" data-id="element-10452"></polyline>
    </svg>;
}